import { getCookie } from './cookieUtils';
import logger from './Logger';

// Utility to format timestamp as YYYY-MM-DDTHH:mm:ss
export function getFormattedTimestamp(dateObj) {
    return dateObj.toISOString().slice(0, 19);
    }

// Send segment and action type to viewers
export function sendSegmentToViewers(
  text,
  isPartial,
  actionType,
  div_id,
  sequence_id,
  create_timestamp,
  deps,
  isCorrected = false,
  start_time = null
) {
    const isStop = actionType === 'stop';

    if (!deps.socketRef.current) {
        logger.warn('WebSocket not available for viewer segment');
      return;
    }
    if (deps.socketRef.current.readyState !== WebSocket.OPEN) {
      logger.warn('WebSocket not ready for viewer segment');
      return;
    }

    // Get conversation_id from state, URL, or cookies (never null)
    let effectiveConversationId = deps.conversationId|| deps.conversationIdFromUrl|| getCookie('conversation_id');
    if (!effectiveConversationId || effectiveConversationId === 'null') {
      // Fallback: try to extract from URL again
      const pathSegments = window.location.pathname.split('/');
      const urlConvId = pathSegments[pathSegments.length - 1];
      if (urlConvId && urlConvId !== 'record' && urlConvId.length > 10) {
        effectiveConversationId = urlConvId;
      }
    }
    // If still not found, do not send segment
    if (!effectiveConversationId || effectiveConversationId === 'null') {
      console.warn('[sendSegmentToViewers] No valid conversation_id, segment not sent');
      return;
    }
     if (deps.previousConversationId && effectiveConversationId === deps.previousConversationId && !isStop) {
      console.warn('[sendSegmentToViewers] Skipping segment to stopped conversation:', deps.previousConversationId);
      return;
    }
    
    // Ensure proper spacing: add leading space for continuation segments (sequence_id > 0)
    // This ensures that when viewer concatenates segments, words are properly separated
    let textToSend = text ? text.trim() : '';
    if (sequence_id > 0 && textToSend.length > 0 && !isCorrected) {
      textToSend = ' ' + textToSend;
    }
    
    const payload = {
      action: 'segment',
      conversation_id: effectiveConversationId,
      text: textToSend,
      is_partial: isPartial,
      is_pause: deps.isPausedRef.current,
      is_stop: isStop,
      is_corrected: isCorrected,
      div_id,
      sequence_id,
      start_time: start_time
    };
    deps.socketRef.current.send(JSON.stringify(payload));
  };

// Function to group segments for display
export function getDisplaySegments(persistentSegments,transcriptSegments,currentDivIdRef) { 
      const persistentDivIds = new Set(persistentSegments.map(seg => seg.div_id));
      persistentDivIds.delete(currentDivIdRef.current);
      const uniqueTranscriptSegments = transcriptSegments.filter(seg => 
        !persistentDivIds.has(seg.div_id)
      );
      // Merge without sorting: persistentSegments first (older), then uniqueTranscriptSegments (newer/live)
      const allSegments = [...persistentSegments, ...uniqueTranscriptSegments];
      
      const displaySegments = [];
      let currentGroup = [];
  
      allSegments.forEach((seg, idx) => {
        if (idx === 0 || seg.shouldStartNewLine) {
          // Start new group when it's the first segment or shouldStartNewLine is true
          if (currentGroup.length > 0) {
            displaySegments.push(currentGroup);
          }
          currentGroup = [seg];
        } else if (idx > 0 && currentGroup.length > 0) {
          // Check if div_id changed from the last segment in current group (additional safety check)
          const lastSegInGroup = currentGroup[currentGroup.length - 1];
          if (lastSegInGroup.div_id && seg.div_id && lastSegInGroup.div_id !== seg.div_id) {
            // div_id changed, start new group
            displaySegments.push(currentGroup);
            currentGroup = [seg];
          } else {
            // Same div_id, add to current group
            currentGroup.push(seg);
          }
        } else {
          // Add to current group - continuation of the same thought
          currentGroup.push(seg);
        }
      });
  
      // Don't forget the last group
      if (currentGroup.length > 0) {
        displaySegments.push(currentGroup);
      }
  
      return displaySegments;
    };

     // Helper to create a segment with correct div_id and sequence_id using sequenceMapRef
     export function createSegment(
          text,
          prevSegments,
          currentDivId,
          setCurrentDivId,
          sequenceMap,
          setSequenceMap,
          isManual,
          deps
        ) {
        const currentTime = new Date();
        let div_id = currentDivId;
        let shouldStartNewLine = false;
        let sequence_id = 0;
    
        if (isManual) {
          // Always generate a new div_id for manual text
          div_id = `${deps.sessionId || 'nosession'}-${deps.generateRandomAlpha()}`;
          setCurrentDivId(div_id);
          sequence_id = 0;
          deps.sequenceMapRef.current[div_id] = 0;
          setSequenceMap(map => ({ ...map, [div_id]: 0 }));
        } else if (prevSegments.length > 0) {
          const lastSegment = prevSegments[prevSegments.length - 1];
          const timeDifference = (currentTime - lastSegment.timestamp) / 1000;
          // Start a new line if enough time has passed OR if the last segment was an image
          shouldStartNewLine = timeDifference >= 5 || lastSegment.type === 'image';
          if (shouldStartNewLine) {
            div_id = deps.getNewDivId();
            setCurrentDivId(div_id);
            sequence_id = 0;
            deps.sequenceMapRef.current[div_id] = 0;
            setSequenceMap(map => ({ ...map, [div_id]: 0 }));
          } else {
            div_id = lastSegment.div_id || currentDivId || deps.getNewDivId();
            const currentSeq = deps.sequenceMapRef.current[div_id] || 0;
            sequence_id = currentSeq + 1;
          }
        } else {
          div_id = deps.getNewDivId();
          setCurrentDivId(div_id);
          sequence_id = 0;
          deps.sequenceMapRef.current[div_id] = 0;
          setSequenceMap(map => ({ ...map, [div_id]: 0 }));
        }
    
        return {
          text: text.trim(),
          div_id,
          sequence_id,
          create_timestamp:deps.getFormattedTimestamp(currentTime),
          timestamp: currentTime,
          timerAtCreation: typeof deps.calculateElapsedTime === 'function' ? deps.calculateElapsedTime() : (deps.timer ?? 0),
          shouldStartNewLine,
          final: isManual
        };
      };